import { createContext, PropsWithChildren, useCallback, useContext, useRef, useState } from 'react';
import { getUserMaterial, UserMaterial } from '../../../api/user-material';
import { getMedialinks, Medialink } from '../../../api/medialink';
import ModuleContext from '../../../pages/material/_contexts/ModuleContext';
import useThrowAsyncError from '../../../hooks/useThrowAsyncError';

type BundleMaterialCacheContextValue = {
  userMaterial: Record<string, UserMaterial>;
  medialinks: Record<string, Medialink>;
  fetchUserMaterial: (ids: string[]) => Promise<void>;
  fetchMedialinks: (ids: string[]) => Promise<void>;
};

export const BundleMaterialCacheContext = createContext<BundleMaterialCacheContextValue>({
  userMaterial: {},
  medialinks: {},
  fetchUserMaterial: async () => {},
  fetchMedialinks: async () => {},
});

export function BundleMaterialCacheProvider({ children }: PropsWithChildren) {
  const { module } = useContext(ModuleContext);
  const [userMaterial, setUserMaterial] = useState<Record<string, UserMaterial>>({});
  const [medialinks, setMedialinks] = useState<Record<string, Medialink>>({});
  const handleError = useThrowAsyncError();

  const requestedUserMaterialIds = useRef(new Set<string>());
  const requestedMedialinkIds = useRef(new Set<string>());

  const fetchUserMaterial = useCallback(
    (ids: string[]) => {
      if (!module) return Promise.resolve();

      const newIds = ids.filter(id => !requestedUserMaterialIds.current.has(id));
      if (newIds.length === 0) return Promise.resolve();

      newIds.forEach(id => requestedUserMaterialIds.current.add(id));

      return getUserMaterial(module.id, {
        ids: newIds.join(','),
        includeScore: true,
        includeNodeHierarchy: true,
        includeSubModules: true,
        includeEmbeds: true,
      })
        .then(data => {
          setUserMaterial(prev => ({ ...prev, ...data.data.reduce((acc, item) => ({ ...acc, [item.id]: item }), {}) }));
        })
        .catch(handleError);
    },
    [handleError, module],
  );

  const fetchMedialinks = useCallback(
    (ids: string[]) => {
      if (!module) return Promise.resolve();

      const newIds = ids.filter(id => !requestedMedialinkIds.current.has(id));
      if (newIds.length === 0) return Promise.resolve();

      newIds.forEach(id => requestedMedialinkIds.current.add(id));

      return getMedialinks(module.id, { ids: newIds.join(','), includeScore: true, includeSubModules: true })
        .then(data => {
          setMedialinks(prev => ({ ...prev, ...data.data.reduce((acc, item) => ({ ...acc, [item.id]: item }), {}) }));
        })
        .catch(handleError);
    },
    [handleError, module],
  );

  return (
    <BundleMaterialCacheContext.Provider value={{ userMaterial, medialinks, fetchUserMaterial, fetchMedialinks }}>
      {children}
    </BundleMaterialCacheContext.Provider>
  );
}
